"use client"

import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Buildings2, MapPoint } from "@solar-icons/react"
import { cn } from "@/lib/utils"

export type CreateBusinessTypeDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Organization root id — required to offer the branch option. */
  parentBusinessId?: string | null
  parentBusinessName?: string | null
}

export function CreateBusinessTypeDialog({
  open,
  onOpenChange,
  parentBusinessId,
  parentBusinessName,
}: CreateBusinessTypeDialogProps) {
  const { t } = useTranslation("businesses")
  const navigate = useNavigate()
  const canCreateBranch = Boolean(parentBusinessId)

  const goStandalone = () => {
    onOpenChange(false)
    navigate("/business/create")
  }

  const goBranch = () => {
    if (!parentBusinessId) return
    onOpenChange(false)
    navigate(`/business/create?parentBusiness=${parentBusinessId}`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("create.chooseType.title")}</DialogTitle>
          <DialogDescription>{t("create.chooseType.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 pt-1">
          <button
            type="button"
            onClick={goStandalone}
            className={cn(
              "flex w-full items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors",
              "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <Buildings2 className="size-5 text-foreground" />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="font-medium">{t("create.chooseType.standaloneTitle")}</p>
              <p className="text-sm text-muted-foreground">
                {t("create.chooseType.standaloneDescription")}
              </p>
            </div>
          </button>

          {canCreateBranch ? (
            <button
              type="button"
              onClick={goBranch}
              className={cn(
                "flex w-full items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors",
                "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
                <MapPoint className="size-5 text-foreground" />
              </div>
              <div className="min-w-0 space-y-1">
                <p className="font-medium">{t("create.chooseType.branchTitle")}</p>
                <p className="text-sm text-muted-foreground">
                  {parentBusinessName
                    ? t("create.chooseType.branchDescription", {
                        name: parentBusinessName,
                      })
                    : t("create.chooseType.branchDescriptionFallback")}
                </p>
              </div>
            </button>
          ) : null}
        </div>

        <div className="flex justify-end pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("create.chooseType.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
